import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Zap, Ship, Loader2, DollarSign, Leaf, AlertTriangle,
  ArrowRight, Plus, Trash2, Trophy, Sparkles, Scale,
  Target, ShieldCheck, Handshake, TrendingDown, ChevronRight, CheckCircle2
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis,
  PolarRadiusAxis, Radar, Legend
} from 'recharts';

const DEAL_COLORS = ['#0A2463', '#E11D48', '#16A34A'];
const DEAL_LABELS = ['Deal A', 'Deal B', 'Deal C'];

const DEFAULT_DEAL = {
  port_load: '', port_discharge: '', cargo_mt: '65000',
  vessel_id: '', speed_knots: '14.5', charter_rate_daily: '80000',
  laytime_hours: '72', demurrage_rate_daily: '45000', carbon_price_per_ton: '80',
};

const PRESETS = [
  { label: 'US Gulf → NE Asia', port_load: 'US Gulf', port_discharge: 'NE Asia', charter_rate_daily: '85000' },
  { label: 'Qatar → NW Europe', port_load: 'Qatar', port_discharge: 'NW Europe', charter_rate_daily: '75000' },
  { label: 'Australia → NE Asia', port_load: 'Australia', port_discharge: 'NE Asia', charter_rate_daily: '65000' },
  { label: 'Nigeria → NW Europe', port_load: 'Nigeria', port_discharge: 'NW Europe', charter_rate_daily: '70000' },
  { label: 'US Gulf → NW Europe', port_load: 'US Gulf', port_discharge: 'NW Europe', charter_rate_daily: '78000' },
  { label: 'Trinidad → NW Europe', port_load: 'Trinidad', port_discharge: 'NW Europe', charter_rate_daily: '68000' },
];

function DealForm({ deal, index, vessels, ports, onChange, onRemove, canRemove }) {
  const color = DEAL_COLORS[index];
  const label = DEAL_LABELS[index];

  const handleField = (key, value) => {
    onChange(index, { ...deal, [key]: value });
  };

  return (
    <Card className="bg-white border-2 rounded-sm shadow-none" style={{ borderColor: `${color}30` }} data-testid={`deal-form-${index}`}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-sm flex items-center justify-center text-white text-[10px] font-bold" style={{ backgroundColor: color }}>
              {String.fromCharCode(65 + index)}
            </div>
            <span className="text-xs font-bold text-slate-800">{label}</span>
          </div>
          {canRemove && (
            <Button variant="ghost" size="sm" onClick={() => onRemove(index)} className="h-6 w-6 p-0 text-slate-400 hover:text-red-500" data-testid={`remove-deal-${index}`}>
              <Trash2 className="h-3 w-3" />
            </Button>
          )}
        </div>
        {/* Quick Preset */}
        <div className="flex flex-wrap gap-1 mb-2">
          {PRESETS.map(p => (
            <button
              key={p.label}
              onClick={() => onChange(index, { ...deal, port_load: p.port_load, port_discharge: p.port_discharge, charter_rate_daily: p.charter_rate_daily })}
              className="text-[9px] px-1.5 py-0.5 border border-slate-200 rounded-sm text-slate-500 hover:bg-slate-50 hover:border-slate-300 transition-colors"
              data-testid={`preset-${index}-${p.port_load.replace(/\s/g, '')}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Load Port</label>
            <Select value={deal.port_load} onValueChange={v => handleField('port_load', v)}>
              <SelectTrigger className="rounded-sm h-7 text-[11px] mt-0.5" data-testid={`port-load-${index}`}>
                <SelectValue placeholder="Select port" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(
                  (ports?.loading || []).reduce((g, p) => { (g[p.region] = g[p.region] || []).push(p); return g; }, {})
                ).map(([region, rp]) => (
                  <SelectGroup key={region}>
                    <SelectLabel className="text-[9px] uppercase tracking-wider text-slate-400">{region}</SelectLabel>
                    {rp.map(p => <SelectItem key={p.id} value={p.name} className="text-[11px]">{p.name}</SelectItem>)}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Discharge Port</label>
            <Select value={deal.port_discharge} onValueChange={v => handleField('port_discharge', v)}>
              <SelectTrigger className="rounded-sm h-7 text-[11px] mt-0.5" data-testid={`port-discharge-${index}`}>
                <SelectValue placeholder="Select port" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(
                  (ports?.discharge || []).reduce((g, p) => { (g[p.region] = g[p.region] || []).push(p); return g; }, {})
                ).map(([region, rp]) => (
                  <SelectGroup key={region}>
                    <SelectLabel className="text-[9px] uppercase tracking-wider text-slate-400">{region}</SelectLabel>
                    {rp.map(p => <SelectItem key={p.id} value={p.name} className="text-[11px]">{p.name}</SelectItem>)}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Cargo (MT)</label>
            <Input type="number" value={deal.cargo_mt} onChange={e => handleField('cargo_mt', e.target.value)} className="rounded-sm h-7 text-[11px] font-mono mt-0.5" data-testid={`cargo-${index}`} />
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Vessel</label>
            <select value={deal.vessel_id} onChange={e => handleField('vessel_id', e.target.value)} className="w-full h-7 rounded-sm border border-slate-200 bg-white px-1.5 text-[11px] mt-0.5" data-testid={`vessel-${index}`}>
              <option value="">TBN</option>
              {vessels.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Charter $/day</label>
            <Input type="number" value={deal.charter_rate_daily} onChange={e => handleField('charter_rate_daily', e.target.value)} className="rounded-sm h-7 text-[11px] font-mono mt-0.5" data-testid={`charter-${index}`} />
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Speed (kn)</label>
            <Input type="number" value={deal.speed_knots} onChange={e => handleField('speed_knots', e.target.value)} className="rounded-sm h-7 text-[11px] font-mono mt-0.5" data-testid={`speed-${index}`} />
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Laytime (h)</label>
            <Input type="number" value={deal.laytime_hours} onChange={e => handleField('laytime_hours', e.target.value)} className="rounded-sm h-7 text-[11px] font-mono mt-0.5" />
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Carbon $/t</label>
            <Input type="number" value={deal.carbon_price_per_ton} onChange={e => handleField('carbon_price_per_ton', e.target.value)} className="rounded-sm h-7 text-[11px] font-mono mt-0.5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function DealComparisonPage() {
  const [vessels, setVessels] = useState([]);
  const [ports, setPorts] = useState({ loading: [], discharge: [] });
  const [deals, setDeals] = useState([
    { ...DEFAULT_DEAL, port_load: 'US Gulf', port_discharge: 'NE Asia', charter_rate_daily: '85000' },
    { ...DEFAULT_DEAL, port_load: 'Qatar', port_discharge: 'NE Asia', charter_rate_daily: '75000' },
  ]);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);

  useEffect(() => {
    Promise.all([api.get('/deals/vessels'), api.get('/deals/ports')])
      .then(([v, p]) => { setVessels(v.data); setPorts(p.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleDealChange = (index, updated) => {
    setDeals(prev => prev.map((d, i) => i === index ? updated : d));
  };

  const handleAddDeal = () => {
    if (deals.length < 3) {
      const next = PRESETS.find(p => !deals.some(d => d.port_load === p.port_load && d.port_discharge === p.port_discharge)) || PRESETS[2];
      setDeals(prev => [...prev, { ...DEFAULT_DEAL, port_load: next.port_load, port_discharge: next.port_discharge, charter_rate_daily: next.charter_rate_daily }]);
    }
  };

  const handleRemoveDeal = (index) => {
    if (deals.length > 2) setDeals(prev => prev.filter((_, i) => i !== index));
  };

  const handleCompare = async () => {
    setComparing(true);
    setComparison(null);
    try {
      const payload = deals.map(d => ({
        ...d,
        cargo_mt: parseFloat(d.cargo_mt) || 65000,
        speed_knots: parseFloat(d.speed_knots) || 14.5,
        charter_rate_daily: parseFloat(d.charter_rate_daily) || 80000,
        laytime_hours: parseFloat(d.laytime_hours) || 72,
        demurrage_rate_daily: parseFloat(d.demurrage_rate_daily) || 45000,
        carbon_price_per_ton: parseFloat(d.carbon_price_per_ton) || 80,
      }));
      const { data } = await api.post('/deals/compare', { deals: payload });
      setComparison(data);
    } catch (e) { console.error(e); }
    finally { setComparing(false); }
  };

  const allValid = deals.every(d => d.port_load && d.port_discharge);

  // Chart data
  const costBarData = (comparison?.deals || []).map((d, i) => ({
    name: DEAL_LABELS[i],
    freight: d.freight_cost,
    carbon: d.carbon_cost,
    hidden: d.total_hidden_costs,
    color: DEAL_COLORS[i],
  }));

  const metricsData = (comparison?.deals || []).length > 0 ? [
    { metric: 'Cost/MMBtu', ...Object.fromEntries((comparison?.deals || []).map((d, i) => [DEAL_LABELS[i], d.cost_per_mmbtu * 100])) },
    { metric: 'CO2e (100t)', ...Object.fromEntries((comparison?.deals || []).map((d, i) => [DEAL_LABELS[i], d.emissions.total_co2e_tons / 100])) },
    { metric: 'Transit Days', ...Object.fromEntries((comparison?.deals || []).map((d, i) => [DEAL_LABELS[i], d.transit_days])) },
    { metric: 'Fuel (100t)', ...Object.fromEntries((comparison?.deals || []).map((d, i) => [DEAL_LABELS[i], d.fuel_consumption_tons / 100])) },
    { metric: 'Hidden $k', ...Object.fromEntries((comparison?.deals || []).map((d, i) => [DEAL_LABELS[i], d.total_hidden_costs / 1000])) },
  ] : [];

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="deal-comparison-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-950">Deal Comparison</h1>
          <p className="text-sm text-slate-500 mt-1">Compare 2-3 alternate routes side-by-side to find the optimal delivery</p>
        </div>
        <div className="flex gap-2">
          {deals.length < 3 && (
            <Button variant="outline" size="sm" onClick={handleAddDeal} data-testid="add-deal-button" className="rounded-sm text-xs border-slate-200">
              <Plus className="h-3.5 w-3.5 mr-1" />Add Deal
            </Button>
          )}
          <Button
            onClick={handleCompare}
            disabled={comparing || !allValid}
            data-testid="compare-deals-button"
            className="rounded-sm bg-[#0A2463] hover:bg-[#0A2463]/90 text-white text-xs"
          >
            {comparing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Scale className="h-3.5 w-3.5 mr-1.5" />}
            Compare Deals
          </Button>
        </div>
      </div>

      {/* Deal Forms */}
      <div className={`grid gap-4 ${deals.length === 3 ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-1 md:grid-cols-2'}`}>
        {deals.map((d, i) => (
          <DealForm
            key={i}
            deal={d}
            index={i}
            vessels={vessels}
            ports={ports}
            onChange={handleDealChange}
            onRemove={handleRemoveDeal}
            canRemove={deals.length > 2}
          />
        ))}
      </div>

      {/* Loading */}
      {comparing && (
        <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
          <CardContent className="py-12 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-[#0A2463]" />
            <p className="text-sm text-slate-500 mt-3">Analyzing {deals.length} deals & generating comparison...</p>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {comparison && (
        <div className="space-y-4" data-testid="comparison-result">
          {/* Winner Banner */}
          <Card className="bg-[#16A34A]/[0.05] border border-[#16A34A]/20 rounded-sm shadow-none" data-testid="comparison-winner">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-sm bg-[#16A34A] flex items-center justify-center">
                  <Trophy className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-xs font-bold text-[#16A34A]">Optimal Deal — Lowest Total Cost</p>
                  <p className="text-sm font-bold text-slate-900 mt-0.5">
                    {comparison.best.lowest_cost.route} — ${comparison.best.lowest_cost.value.toLocaleString()}
                  </p>
                  {comparison.savings_potential > 0 && (
                    <p className="text-xs text-slate-500 mt-0.5">
                      Saves <span className="font-bold text-[#16A34A] font-mono">${comparison.savings_potential.toLocaleString()}</span> vs most expensive option
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Side-by-Side Comparison Table */}
          <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-800">Side-by-Side Comparison</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm" data-testid="comparison-table">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-4 py-2 w-36">Metric</th>
                    {comparison.deals.map((d, i) => (
                      <th key={i} className="text-right text-[10px] uppercase tracking-wider font-semibold px-4 py-2" style={{ color: DEAL_COLORS[i] }}>
                        {DEAL_LABELS[i]}: {d.route}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: 'Distance', key: 'distance_nm', fmt: v => `${v?.toLocaleString()} NM` },
                    { label: 'Transit', key: 'transit_days', fmt: v => `${v} days` },
                    { label: 'Fuel Used', key: 'fuel_consumption_tons', fmt: v => `${v?.toLocaleString()} t` },
                    { label: 'Freight', key: 'freight_cost', fmt: v => `$${v?.toLocaleString()}`, highlight: 'min' },
                    { label: 'Carbon Cost', key: 'carbon_cost', fmt: v => `$${v?.toLocaleString()}`, highlight: 'min' },
                    { label: 'Demurrage Exp.', key: 'demurrage_exposure', fmt: v => `$${v?.toLocaleString()}` },
                    { label: 'Hidden Costs', key: 'total_hidden_costs', fmt: v => `$${v?.toLocaleString()}` },
                    { label: 'True Total', key: 'true_total_cost', fmt: v => `$${v?.toLocaleString()}`, highlight: 'min', bold: true },
                    { label: '$/MMBtu', key: 'cost_per_mmbtu', fmt: v => `$${v?.toFixed(4)}`, highlight: 'min', bold: true },
                    { label: 'Total CO2e', key: null, custom: d => `${d.emissions.total_co2e_tons.toLocaleString()} t`, highlight: 'min_em' },
                    { label: 'CO2e/MMBtu', key: null, custom: d => d.emissions.co2e_per_mmbtu.toFixed(6), highlight: 'min_em2' },
                    { label: 'EEOI', key: null, custom: d => d.emissions.eeoi.toFixed(2) },
                  ].map(row => {
                    const values = comparison.deals.map(d => row.key ? d[row.key] : (row.custom ? parseFloat(row.custom(d).replace(/[^0-9.-]/g, '')) : 0));
                    const minVal = Math.min(...values.filter(v => v > 0));
                    return (
                      <tr key={row.label} className="border-b border-slate-50 hover:bg-slate-50/50">
                        <td className={`px-4 py-2 text-xs ${row.bold ? 'font-bold text-slate-900' : 'text-slate-600'}`}>{row.label}</td>
                        {comparison.deals.map((d, i) => {
                          const val = row.key ? d[row.key] : null;
                          const display = row.custom ? row.custom(d) : row.fmt(val);
                          const isMin = row.highlight && values[i] === minVal && values[i] > 0;
                          return (
                            <td key={i} className={`px-4 py-2 text-xs font-mono text-right ${row.bold ? 'font-bold' : ''} ${isMin ? 'text-[#16A34A] font-bold' : 'text-slate-700'}`}>
                              {display}
                              {isMin && <Trophy className="h-3 w-3 inline ml-1 text-[#16A34A]" />}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Cost Stacked Bar */}
            <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-800">Cost Structure Comparison</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={costBarData} margin={{ left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#334155', fontWeight: 700 }} />
                      <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={v => `$${(v / 1e6).toFixed(1)}M`} />
                      <RechartsTooltip contentStyle={{ borderRadius: 2, border: '1px solid #e2e8f0', fontSize: 12 }} formatter={v => `$${v.toLocaleString()}`} />
                      <Bar dataKey="freight" name="Freight" fill="#0A2463" stackId="a" barSize={36} />
                      <Bar dataKey="carbon" name="Carbon" fill="#16A34A" stackId="a" />
                      <Bar dataKey="hidden" name="Hidden" fill="#F59E0B" stackId="a" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Radar */}
            {metricsData.length > 0 && (
              <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-slate-800">Multi-Metric Radar</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <RadarChart data={metricsData} outerRadius={80}>
                        <PolarGrid stroke="#e2e8f0" />
                        <PolarAngleAxis dataKey="metric" tick={{ fontSize: 10, fill: '#64748b' }} />
                        <PolarRadiusAxis tick={false} />
                        {comparison.deals.map((_, i) => (
                          <Radar
                            key={i}
                            name={DEAL_LABELS[i]}
                            dataKey={DEAL_LABELS[i]}
                            stroke={DEAL_COLORS[i]}
                            fill={DEAL_COLORS[i]}
                            fillOpacity={0.1}
                            strokeWidth={2}
                          />
                        ))}
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* AI Recommendation */}
          {comparison.ai_recommendation && (
            <Card className="bg-white border border-[#0A2463]/20 rounded-sm shadow-none" data-testid="comparison-ai-recommendation">
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-sm bg-[#0A2463]/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Sparkles className="h-4 w-4 text-[#0A2463]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-[#0A2463] mb-1.5">AI Strategy Recommendation</p>
                    <div className="text-xs text-slate-700 leading-relaxed whitespace-pre-wrap">
                      {comparison.ai_recommendation}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Structured Actionable Insights Panel */}
          {comparison.ai_insights && Object.keys(comparison.ai_insights).length > 0 && (
            <Card className="bg-white border border-slate-200 rounded-sm shadow-none" data-testid="ai-insights-panel">
              <CardHeader className="pb-2 border-b border-slate-100">
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-sm bg-[#0A2463] flex items-center justify-center">
                    <Target className="h-3.5 w-3.5 text-white" />
                  </div>
                  <CardTitle className="text-sm font-bold text-slate-900">Actionable Intelligence</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {comparison.ai_insights.winner_summary && (
                  <div className="px-5 py-3 bg-[#16A34A]/[0.04] border-b border-slate-100" data-testid="insight-winner">
                    <div className="flex items-center gap-2 mb-1">
                      <Trophy className="h-3.5 w-3.5 text-[#16A34A]" />
                      <span className="text-[9px] uppercase tracking-[0.15em] font-bold text-[#16A34A]">Winner</span>
                    </div>
                    <p className="text-xs text-slate-800 font-medium leading-relaxed">{comparison.ai_insights.winner_summary}</p>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-100">
                  {/* Cost Optimization */}
                  {comparison.ai_insights.cost_optimization?.length > 0 && (
                    <div className="p-4" data-testid="insight-cost">
                      <div className="flex items-center gap-2 mb-2.5">
                        <div className="w-5 h-5 rounded-sm bg-emerald-50 flex items-center justify-center">
                          <TrendingDown className="h-3 w-3 text-emerald-600" />
                        </div>
                        <span className="text-[9px] uppercase tracking-[0.15em] font-bold text-slate-500">Cost Optimization</span>
                      </div>
                      <ul className="space-y-2">
                        {comparison.ai_insights.cost_optimization.map((item, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <ChevronRight className="h-3 w-3 text-emerald-500 mt-0.5 shrink-0" />
                            <span className="text-[11px] text-slate-700 leading-relaxed">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {/* Emissions Strategy */}
                  {comparison.ai_insights.emissions_strategy?.length > 0 && (
                    <div className="p-4" data-testid="insight-emissions">
                      <div className="flex items-center gap-2 mb-2.5">
                        <div className="w-5 h-5 rounded-sm bg-blue-50 flex items-center justify-center">
                          <Leaf className="h-3 w-3 text-blue-600" />
                        </div>
                        <span className="text-[9px] uppercase tracking-[0.15em] font-bold text-slate-500">Emissions Strategy</span>
                      </div>
                      <ul className="space-y-2">
                        {comparison.ai_insights.emissions_strategy.map((item, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <ChevronRight className="h-3 w-3 text-blue-500 mt-0.5 shrink-0" />
                            <span className="text-[11px] text-slate-700 leading-relaxed">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-100 border-t border-slate-100">
                  {/* Risk Mitigations */}
                  {comparison.ai_insights.risk_mitigations?.length > 0 && (
                    <div className="p-4" data-testid="insight-risk">
                      <div className="flex items-center gap-2 mb-2.5">
                        <div className="w-5 h-5 rounded-sm bg-amber-50 flex items-center justify-center">
                          <ShieldCheck className="h-3 w-3 text-amber-600" />
                        </div>
                        <span className="text-[9px] uppercase tracking-[0.15em] font-bold text-slate-500">Risk Mitigations</span>
                      </div>
                      <ul className="space-y-2">
                        {comparison.ai_insights.risk_mitigations.map((item, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <ChevronRight className="h-3 w-3 text-amber-500 mt-0.5 shrink-0" />
                            <span className="text-[11px] text-slate-700 leading-relaxed">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {/* Negotiation Leverage */}
                  {comparison.ai_insights.negotiation_leverage?.length > 0 && (
                    <div className="p-4" data-testid="insight-negotiation">
                      <div className="flex items-center gap-2 mb-2.5">
                        <div className="w-5 h-5 rounded-sm bg-violet-50 flex items-center justify-center">
                          <Handshake className="h-3 w-3 text-violet-600" />
                        </div>
                        <span className="text-[9px] uppercase tracking-[0.15em] font-bold text-slate-500">Negotiation Leverage</span>
                      </div>
                      <ul className="space-y-2">
                        {comparison.ai_insights.negotiation_leverage.map((item, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <ChevronRight className="h-3 w-3 text-violet-500 mt-0.5 shrink-0" />
                            <span className="text-[11px] text-slate-700 leading-relaxed">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Per-Deal Action Cards */}
          {comparison.deal_actions?.length > 0 && (
            <div data-testid="deal-actions-section">
              <h2 className="text-sm font-bold text-slate-800 mb-3">Per-Deal Action Plans</h2>
              <div className={`grid gap-3 ${comparison.deal_actions.length === 3 ? 'grid-cols-1 lg:grid-cols-3' : 'grid-cols-1 lg:grid-cols-2'}`}>
                {comparison.deal_actions.map((da, i) => {
                  const color = DEAL_COLORS[i];
                  const verdictConfig = {
                    RECOMMENDED: { bg: 'bg-[#16A34A]', text: 'text-white' },
                    COMPETITIVE: { bg: 'bg-[#0A2463]', text: 'text-white' },
                    EXPENSIVE: { bg: 'bg-[#E11D48]', text: 'text-white' },
                  };
                  const vc = verdictConfig[da.verdict] || verdictConfig.COMPETITIVE;
                  return (
                    <Card key={i} className="bg-white border-2 rounded-sm shadow-none" style={{ borderColor: `${color}25` }} data-testid={`deal-action-${i}`}>
                      <CardContent className="p-0">
                        {/* Header */}
                        <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100" style={{ backgroundColor: `${color}06` }}>
                          <div className="flex items-center gap-2">
                            <div className="w-5 h-5 rounded-sm flex items-center justify-center text-white text-[10px] font-bold" style={{ backgroundColor: color }}>
                              {String.fromCharCode(65 + i)}
                            </div>
                            <span className="text-xs font-bold text-slate-800">{da.route}</span>
                          </div>
                          <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm ${vc.bg} ${vc.text}`} data-testid={`deal-verdict-${i}`}>
                            {da.verdict}
                          </span>
                        </div>
                        {/* Optimization Tips */}
                        {da.optimization_tips?.length > 0 && (
                          <div className="px-4 py-3 border-b border-slate-50">
                            <p className="text-[9px] uppercase tracking-[0.15em] font-bold text-emerald-600 mb-1.5">Optimization Tips</p>
                            <ul className="space-y-1.5">
                              {da.optimization_tips.map((t, ti) => (
                                <li key={ti} className="flex items-start gap-1.5">
                                  <CheckCircle2 className="h-3 w-3 text-emerald-400 mt-0.5 shrink-0" />
                                  <span className="text-[11px] text-slate-600 leading-relaxed">{t}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {/* Risk Factors */}
                        {da.risk_factors?.length > 0 && (
                          <div className="px-4 py-3 border-b border-slate-50">
                            <p className="text-[9px] uppercase tracking-[0.15em] font-bold text-amber-600 mb-1.5">Risk Factors</p>
                            <ul className="space-y-1.5">
                              {da.risk_factors.map((r, ri) => (
                                <li key={ri} className="flex items-start gap-1.5">
                                  <AlertTriangle className="h-3 w-3 text-amber-400 mt-0.5 shrink-0" />
                                  <span className="text-[11px] text-slate-600 leading-relaxed">{r}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {/* Negotiation Points */}
                        {da.negotiation_points?.length > 0 && (
                          <div className="px-4 py-3">
                            <p className="text-[9px] uppercase tracking-[0.15em] font-bold text-violet-600 mb-1.5">Negotiation Points</p>
                            <ul className="space-y-1.5">
                              {da.negotiation_points.map((n, ni) => (
                                <li key={ni} className="flex items-start gap-1.5">
                                  <Handshake className="h-3 w-3 text-violet-400 mt-0.5 shrink-0" />
                                  <span className="text-[11px] text-slate-600 leading-relaxed">{n}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {/* Best-In-Class Badges */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
              <CardContent className="p-3 flex items-center gap-3">
                <div className="w-8 h-8 rounded-sm bg-[#16A34A] flex items-center justify-center">
                  <DollarSign className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Lowest Cost</p>
                  <p className="text-xs font-bold text-slate-900">{comparison.best.lowest_cost.route}</p>
                  <p className="text-[10px] font-mono text-[#16A34A]">${comparison.best.lowest_cost.value.toLocaleString()}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
              <CardContent className="p-3 flex items-center gap-3">
                <div className="w-8 h-8 rounded-sm bg-[#007AFF] flex items-center justify-center">
                  <Leaf className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Lowest Emissions</p>
                  <p className="text-xs font-bold text-slate-900">{comparison.best.lowest_emissions.route}</p>
                  <p className="text-[10px] font-mono text-[#007AFF]">{comparison.best.lowest_emissions.value.toLocaleString()} tCO2e</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
              <CardContent className="p-3 flex items-center gap-3">
                <div className="w-8 h-8 rounded-sm bg-[#F59E0B] flex items-center justify-center">
                  <Trophy className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Best $/MMBtu</p>
                  <p className="text-xs font-bold text-slate-900">{comparison.best.lowest_per_mmbtu.route}</p>
                  <p className="text-[10px] font-mono text-[#F59E0B]">${comparison.best.lowest_per_mmbtu.value.toFixed(4)}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
